<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcMeterConsumptions',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    meter: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const items = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    items: items.value,
    ux: ux.value,
  }));

  const $$load_items = () => {
    return props.meter.consumptions
      ? props.meter.consumptions.data
      : $fn.value.fetch('list_meter_consumptions', {
          parameters: { meter_id: props.meter.id },
        });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'items' }, { name: 'ux' }],
    loaders: { items: $$load_items },
    ux: {},
    dataRefs: { items, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.meter,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('meter');
    }
  );

  const act_8320016629450276_emit_3wmipxplu_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_8320016629450276_close_ev6m4mz8p_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_8320016629450276_showModal_mkdldf1wq_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'MeterConsumption',
      props: {
        meter: props.meter,
      },
    };
  };
  const act_7989141091456081_emit_mhopvt1b7_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_7989141091456081_close_lcl499xv8_getWhen = async ($event) => {
    return !!$event.data.modalResult.ok;
  };
  const act_7989141091456081_showModal_ql8uxgy34_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'MeterConsumption',
      props: {
        oid: $event.scope.item.id,
        meter: props.meter,
      },
    };
  };

  const act_8320016629450276_emit_3wmipxplu = new actionTypes.EmitAction({
    actionArgs: act_8320016629450276_emit_3wmipxplu_getActionArgs,
    displayName: 'emitReload',
  });
  const inst_evh_8320016629450276_close_ev6m4mz8p =
    new actionTypes.EventHandlerImpl({
      action: act_8320016629450276_emit_3wmipxplu,
      event: 'close',
      when: inst_evh_8320016629450276_close_ev6m4mz8p_getWhen,
      displayName: 'emitReload',
    });
  const act_8320016629450276_showModal_mkdldf1wq =
    new actionTypes.ShowModalAction({
      actionArgs: act_8320016629450276_showModal_mkdldf1wq_getActionArgs,
      events: [inst_evh_8320016629450276_close_ev6m4mz8p],
    });
  const inst_evh_8320016629450276_clickPreventStop_w3oqzz49q =
    new actionTypes.EventHandlerImpl({
      action: act_8320016629450276_showModal_mkdldf1wq,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_8320016629450276_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8320016629450276_clickPreventStop_w3oqzz49q],
      event: 'click.prevent.stop',
    });
  const act_7989141091456081_emit_mhopvt1b7 = new actionTypes.EmitAction({
    actionArgs: act_7989141091456081_emit_mhopvt1b7_getActionArgs,
    displayName: 'emitReload',
  });
  const inst_evh_7989141091456081_close_lcl499xv8 =
    new actionTypes.EventHandlerImpl({
      action: act_7989141091456081_emit_mhopvt1b7,
      event: 'close',
      when: inst_evh_7989141091456081_close_lcl499xv8_getWhen,
      displayName: 'emitReload',
    });
  const act_7989141091456081_showModal_ql8uxgy34 =
    new actionTypes.ShowModalAction({
      actionArgs: act_7989141091456081_showModal_ql8uxgy34_getActionArgs,
      events: [inst_evh_7989141091456081_close_lcl499xv8],
    });
  const inst_evh_7989141091456081_clickPreventStop_uxr0jbswe =
    new actionTypes.EventHandlerImpl({
      action: act_7989141091456081_showModal_ql8uxgy34,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_7989141091456081_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7989141091456081_clickPreventStop_uxr0jbswe],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_8320016629450276_close_ev6m4mz8p,
    emitReload: inst_evh_8320016629450276_close_ev6m4mz8p,
    inst_evh_8320016629450276_clickPreventStop_w3oqzz49q,
    inst_evh_8320016629450276_clickPreventStop,
    inst_evh_7989141091456081_close_lcl499xv8,
    inst_evh_7989141091456081_clickPreventStop_uxr0jbswe,
    inst_evh_7989141091456081_clickPreventStop,
  };

  const evh_8320016629450276_close_ev6m4mz8p = async (event, scope) => {
    return await actions.inst_evh_8320016629450276_close_ev6m4mz8p.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_clickPreventStop_w3oqzz49q = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8320016629450276_clickPreventStop_w3oqzz49q.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_8320016629450276_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7989141091456081_close_lcl499xv8 = async (event, scope) => {
    return await actions.inst_evh_7989141091456081_close_lcl499xv8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7989141091456081_clickPreventStop_uxr0jbswe = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7989141091456081_clickPreventStop_uxr0jbswe.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7989141091456081_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_7989141091456081_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpWidgetCard>
    <template #header>
      <ItpBox alignItems="center">
        <strong>
          {{ pgettext('MeterConsumptions', 'Meter Consumptions') }}
        </strong>
        <ItpButton
          :text="pgettext('MeterConsumptions', 'Add consumption...')"
          variant="light"
          size="sm"
          class="ms-auto"
          @click.prevent.stop="
            evh_8320016629450276_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </ItpBox>
    </template>
    <BListGroup v-if="loaded">
      <BListGroupItem v-if="!items.length">
        <span>
          {{ pgettext('MeterConsumptions', 'No consumptions.') }}
        </span>
      </BListGroupItem>
      <BListGroupItem
        :key="item.id"
        class="d-flex flex-column"
        v-for="item in items"
      >
        <ItpBox alignItems="start">
          <ItpBox direction="column" class="w-100">
            <ItpBox class="W-100">
              <ItpBox direction="column" class="col-md-2">
                <strong>
                  {{ date(item.start.date) }}
                </strong>
                <small>
                  {{ number(item.start.reading) }} {{ item.unit }}
                </small>
              </ItpBox>
              <ItpBox direction="column" class="col-md-2">
                <strong>
                  {{ date(item.end.date) }}
                </strong>
                <small> {{ number(item.end.reading) }} {{ item.unit }} </small>
              </ItpBox>
              <ItpBox direction="column" class="col-md-2">
                <strong>
                  {{ number(item.total_consumption) }} {{ item.unit }}
                </strong>
              </ItpBox>
              <ItpBox alignItems="center" direction="column" class="col-md-2">
                <ItpBadge
                  :title="gettext('Billing factor')"
                  no-box-shadow
                  variant="light"
                  class="mx-3"
                  v-if="item.billing_factor"
                >
                  <ItpIcon name="fa-times"> </ItpIcon>
                  <ItpText :text="item.billing_factor"> </ItpText>
                </ItpBadge>
                <ItpBadge
                  variant="warning"
                  :text="pgettext('RentalService', 'Non billable')"
                  no-box-shadow
                  class="mx-3 mt-2"
                  v-if="item.non_billable"
                >
                </ItpBadge>
              </ItpBox>
              <ItpBox
                alignItems="start"
                wrap-items
                class="col-md-2"
                v-if="item.meter"
              >
                <UcCompactMeterDisplay :payload="item.meter" class="ps-3">
                </UcCompactMeterDisplay>
              </ItpBox>
              <ItpBox
                alignItems="start"
                wrap-items
                class="col-md-2"
                v-if="item.consumer"
              >
                <UcCompactCompanyDisplay :payload="item.consumer" class="ps-3">
                </UcCompactCompanyDisplay>
              </ItpBox>
            </ItpBox>
            <ItpBox>
              <div class="col-md-7">
                <ItpText :text="item.note" pre v-if="item.note"> </ItpText>
              </div>
            </ItpBox>
          </ItpBox>
          <ItpButton
            icon="fa-pen"
            :tooltip="gettext('Edit')"
            variant="light"
            size="sm"
            class="ms-auto"
            @click.prevent.stop="
              evh_7989141091456081_clickPreventStop($event, { item })
            "
          >
          </ItpButton>
        </ItpBox>
      </BListGroupItem>
    </BListGroup>
  </ItpWidgetCard>
</template>
